import type { VisualAssetDefinition } from '../../visualAssets/catalog';

const STORAGE_KEY = 'mmo:adminLocalVisualAssets';
const LOCAL_PREFIX = 'local:';

interface PersistedLocalVisualAsset {
  readonly id: string;
  readonly label: string;
  readonly imagePath: string;
  readonly drawScale: number;
  readonly yOffsetTiles: number;
  readonly animated?: boolean;
}

export function isLocalVisualAssetId(assetId: string): boolean {
  return assetId.startsWith(LOCAL_PREFIX);
}

export function loadLocalVisualAssets(): VisualAssetDefinition[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as PersistedLocalVisualAsset[];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .filter((asset) => typeof asset.id === 'string' && typeof asset.label === 'string' && typeof asset.imagePath === 'string')
      .map((asset) => ({
        id: asset.id,
        label: asset.label,
        imagePath: asset.imagePath,
        drawScale: Number.isFinite(asset.drawScale) ? asset.drawScale : 1.25,
        yOffsetTiles: Number.isFinite(asset.yOffsetTiles) ? asset.yOffsetTiles : 0,
        animated: asset.animated === true,
      }));
  } catch {
    return [];
  }
}

export function saveLocalVisualAssets(assets: readonly VisualAssetDefinition[]): void {
  const persisted = assets.map((asset) => ({
      id: asset.id,
      label: asset.label,
      imagePath: asset.imagePath,
      drawScale: asset.drawScale,
      yOffsetTiles: asset.yOffsetTiles,
      animated: asset.animated,
    }));
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
}

export async function fileToLocalVisualAsset(
  file: File,
  existing?: VisualAssetDefinition | null,
): Promise<VisualAssetDefinition> {
  const imagePath = await readFileAsDataUrl(file);
  const baseName = file.name.replace(/\.[^.]+$/, '').trim() || 'Local Asset';
  const slug = baseName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'local-asset';
  return {
    id: existing?.id ?? `${LOCAL_PREFIX}${slug}-${Math.random().toString(36).slice(2, 8)}`,
    label: existing?.label ?? baseName,
    imagePath,
    drawScale: existing?.drawScale ?? 1.25,
    yOffsetTiles: existing?.yOffsetTiles ?? 0,
    animated: existing?.animated ?? false,
  };
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error(`Failed to read ${file.name}`));
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error(`Failed to load ${file.name}`));
        return;
      }
      resolve(reader.result);
    };
    reader.readAsDataURL(file);
  });
}
